import { useRef, useState } from 'react';
import { uploadFile } from 'zite-file-upload-sdk';
import { Button } from '@/components/ui/button';
import { Upload, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface DocUploadProps {
  label: string;
  hint: string;
  docKey: string;
  value: string;
  onUpload: (key: string, url: string) => void;
}

function DocUpload({ label, hint, docKey, value, onUpload }: DocUploadProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const { fileUrl } = await uploadFile({ data: file, filename: file.name });
      onUpload(docKey, fileUrl);
      toast.success(`${label} uploaded`);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <div className="border border-border rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
        </div>
        {value
          ? <div className="flex items-center gap-1.5 text-green-500 text-xs font-medium shrink-0"><CheckCircle className="w-4 h-4" /> Uploaded</div>
          : (
            <>
              <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.heic" capture="environment" className="hidden" onChange={handleFile} />
              <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={uploading} className="shrink-0 text-xs">
                {uploading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Uploading</> : <><Upload className="w-3.5 h-3.5 mr-1" /> Upload</>}
              </Button>
            </>
          )}
      </div>
    </div>
  );
}

interface Props { formData: Record<string, string>; onUpdate: (key: string, val: string) => void; }

const DOCS = [
  { key: 'passportUrl', label: 'Passport / Right to Work', hint: 'Photo page of passport or share code for overseas workers', required: true },
  { key: 'proofOfAddressUrl', label: 'Proof of Address', hint: 'Utility bill or bank statement dated within 3 months', required: false },
  { key: 'drivingLicenceUrl', label: 'Driving Licence (optional)', hint: 'Front of driving licence if you have one', required: false },
  { key: 'p45Url', label: 'P45 (optional)', hint: 'From your previous employer — helps payroll set up your tax code', required: false },
  { key: 'qualificationsUrl', label: 'Qualifications / Certifications (optional)', hint: 'Any relevant certificates (food hygiene, personal licence, etc.)', required: false },
];

export default function Step6Documents({ formData, onUpdate }: Props) {
  return (
    <div className="space-y-3">
      <div className="bg-primary/5 border border-primary/20 rounded-lg px-4 py-3 text-xs text-foreground/80 mb-4">
        📱 <strong>On mobile?</strong> Tap "Upload" and choose to take a photo directly. Files can be PDFs, JPEGs, or PNGs. Right to Work documentation is legally required before your first shift.
      </div>
      {DOCS.map(doc => (
        <DocUpload key={doc.key} docKey={doc.key} label={doc.label} hint={doc.hint} value={formData[doc.key] || ''} onUpload={onUpdate} />
      ))}
    </div>
  );
}
